import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Rest, RestService } from '@ralba/shared';
import { EmployeeHints } from '@tpa/api';
import { UserTenanciesSelectors } from '@tpa/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { EMPTY_HINTS } from './hint.model';

/**
 * Loads the "already entered" address / children / other-person hints for an employee and offers
 * them back for quick-fill.
 *
 * Nothing is retained across time: only requests that are still *in flight* are shared (keyed by
 * `tenant:owner`), so the many buttons rendered across one form still cost a single backend call,
 * while every later read — a new visit of the employee card or of an RZD form, and every re-opened
 * picker — re-reads the history instead of replaying a snapshot taken earlier in the session.
 *
 * Uses the app's {@link RestService} (targets the configured backend base URL and unwraps ABP's
 * `{ result }` envelope) — the same pattern as every other `*HttpApiService` — rather than the raw
 * generated `EmployeeHintsServiceProxy`, which resolves a relative base URL and doesn't unwrap.
 */
@Injectable({ providedIn: 'root' })
export class EmployeeHintsService {
  private readonly inFlight = new Map<string, Observable<EmployeeHints>>();

  constructor(
    private readonly rest: RestService,
    private readonly store: Store
  ) {}

  /**
   * Returns the hints for the given owner. When `ownerId` is omitted (self-service surfaces) the
   * current user is used. Emits {@link EMPTY_HINTS} when there is no tenant/owner context yet.
   */
  getHints(ownerId?: number | null): Observable<EmployeeHints> {
    return this.store.select(UserTenanciesSelectors.selectUserIdentifer).pipe(
      take(1),
      switchMap((identifier) => {
        const tenantId = identifier?.tenantId ?? null;
        const userId = ownerId ?? identifier?.userId ?? null;
        if (tenantId == null || userId == null) {
          return of(EMPTY_HINTS);
        }
        return this.load(userId, tenantId);
      })
    );
  }

  /**
   * Stops sharing whatever is currently in flight, so even a subscriber that arrives during an
   * ongoing request gets a brand-new read (e.g. right after an approval changed the history).
   */
  invalidate(): void {
    this.inFlight.clear();
  }

  private load(userId: number, tenantId: number): Observable<EmployeeHints> {
    const key = `${tenantId}:${userId}`;
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }
    const httpRequest: Rest.Request<null> = {
      method: 'GET',
      url: '/api/employee-hint/get',
      params: { userId, tenantId },
    };
    const request = this.rest.request<null, Rest.AbpResponse<EmployeeHints>>(httpRequest).pipe(
      map((response) => response?.result ?? EMPTY_HINTS),
      // A transient failure is reported as "no history" to this subscriber only; the entry is
      // dropped below like any settled request, so the next read retries.
      catchError(() => of(EMPTY_HINTS)),
      // Share only until the call settles: everything subscribing while it is in flight joins it,
      // anything later issues a fresh request.
      finalize(() => {
        if (this.inFlight.get(key) === request) {
          this.inFlight.delete(key);
        }
      }),
      shareReplay(1)
    );
    this.inFlight.set(key, request);
    return request;
  }
}
